import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Toolbar,
  AppBar,
  IconButton,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  AlertColor,
  Tooltip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import api from '../services/api';
import { getUserData } from '../utils/cookies';
import { PageName, ApiResponse, User } from '../types';
import {
  DrawerNavigation,
  DRAWER_WIDTH,
  COLLAPSED_WIDTH,
  ConfirmDialog,
} from '../components';

// Types for series/list response
interface SeriesListResponse {
  makes: { [makeId: string]: { label: string } };
  series: { [serieId: string]: { label: string; make: string } };
  models: { [modelId: string]: { label: string; serie: string } };
}

// UI types
interface MakeItem {
  id: string;
  label: string;
}

interface SerieItem {
  id: string;
  label: string;
  make: string;
  modelCount: number;
}

interface ModelItem {
  id: string;
  label: string;
  serie: string;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: AlertColor;
}

interface SeriesEditorProps {
  onLogout: () => void;
  onNavigate: (page: PageName) => void;
}

function SeriesEditor({ onLogout, onNavigate }: SeriesEditorProps) {
  // UI States
  const [mobileOpen, setMobileOpen] = useState<boolean>(false);
  const [drawerCollapsed, setDrawerCollapsed] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  // Data states
  const [makes, setMakes] = useState<MakeItem[]>([]);
  const [series, setSeries] = useState<SerieItem[]>([]);
  const [models, setModels] = useState<ModelItem[]>([]);

  // Selection states
  const [selectedMakeId, setSelectedMakeId] = useState<string | null>(null);
  const [selectedSerieId, setSelectedSerieId] = useState<string | null>(null);
  const [selectedModelIds, setSelectedModelIds] = useState<string[]>([]);

  // Search states
  const [makeSearch, setMakeSearch] = useState('');
  const [serieSearch, setSerieSearch] = useState('');

  // Create serie dialog
  const [createDialogOpen, setCreateDialogOpen] = useState<boolean>(false);
  const [newSerieLabel, setNewSerieLabel] = useState<string>('');
  const [creating, setCreating] = useState<boolean>(false);

  // Delete serie dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [serieToDelete, setSerieToDelete] = useState<SerieItem | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);

  // Rename serie
  const [renamingSerieId, setRenamingSerieId] = useState<string | null>(null);
  const [renameSerieLabel, setRenameSerieLabel] = useState('');
  const [renamingSerie, setRenamingSerie] = useState(false);

  // Move models dialog
  const [moveDialogOpen, setMoveDialogOpen] = useState<boolean>(false);
  const [targetSerieId, setTargetSerieId] = useState<string>('');
  const [moving, setMoving] = useState<boolean>(false);

  // Create serie & move dialog
  const [createMoveDialogOpen, setCreateMoveDialogOpen] = useState<boolean>(false);
  const [createMoveSerieLabel, setCreateMoveSerieLabel] = useState<string>('');
  const [creatingAndMoving, setCreatingAndMoving] = useState<boolean>(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Ref for preventing double fetch
  const hasFetched = useRef(false);

  // Load user data
  useEffect(() => {
    const userData = getUserData();
    if (userData) {
      setUser({
        id: userData.id,
        username: userData.username,
        firstName: userData.firstName,
        lastName: userData.lastName,
        roles: userData.roles || [],
      });
    }
  }, []);

  // Load series data
  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await api.get<ApiResponse<SeriesListResponse>>('series/list');
      const data = response.data.results;

      // Transform makes (sorted alphabetically)
      const makesArray: MakeItem[] = Object.entries(data.makes)
        .map(([id, make]) => ({ id, label: make.label }))
        .sort((a, b) => a.label.localeCompare(b.label));
      setMakes(makesArray);

      // Transform models (for counting per serie)
      const modelsArray: ModelItem[] = Object.entries(data.models)
        .map(([id, model]) => ({ id, label: model.label, serie: model.serie }));
      setModels(modelsArray);

      // Count models per serie
      const modelCounts: { [serieId: string]: number } = {};
      modelsArray.forEach((model) => {
        modelCounts[model.serie] = (modelCounts[model.serie] || 0) + 1;
      });

      // Transform series (sorted alphabetically)
      const seriesArray: SerieItem[] = Object.entries(data.series)
        .map(([id, serie]) => ({
          id,
          label: serie.label,
          make: serie.make,
          modelCount: modelCounts[id] || 0,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
      setSeries(seriesArray);

    } catch (err) {
      console.error('Failed to load series data:', err);
      setError('Failed to load series data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchData();
  }, []);

  // Makes with at least one empty serie
  const makesWithEmptySeries = useMemo(() => {
    const set = new Set<string>();
    for (const s of series) {
      if (s.modelCount === 0) set.add(s.make);
    }
    return set;
  }, [series]);

  // Filter makes by search
  const filteredMakes = useMemo(() => {
    if (!makeSearch.trim()) return makes;
    const q = makeSearch.toLowerCase();
    return makes.filter((m) => m.label.toLowerCase().includes(q));
  }, [makes, makeSearch]);

  // Filter series by selected make and search
  const filteredSeries = useMemo(() => {
    if (!selectedMakeId) return [];
    let result = series.filter((s) => s.make === selectedMakeId);
    if (serieSearch.trim()) {
      const q = serieSearch.toLowerCase();
      result = result.filter((s) => s.label.toLowerCase().includes(q));
    }
    return result;
  }, [series, selectedMakeId, serieSearch]);

  // Filter models by selected serie
  const filteredModels = useMemo(() => {
    if (!selectedSerieId) return [];
    return models
      .filter((m) => m.serie === selectedSerieId)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [models, selectedSerieId]);

  // Get series available for moving (same make, different serie)
  const availableTargetSeries = useMemo(() => {
    if (!selectedMakeId || !selectedSerieId) return [];
    return series
      .filter((s) => s.make === selectedMakeId && s.id !== selectedSerieId)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [series, selectedMakeId, selectedSerieId]);

  // Handlers
  const handleMakeClick = (makeId: string) => {
    setSelectedMakeId(makeId);
    setSelectedSerieId(null);
    setSelectedModelIds([]);
    setSerieSearch('');
  };

  const handleSerieClick = (serieId: string) => {
    setSelectedSerieId(serieId);
    setSelectedModelIds([]);
  };

  const handleModelToggle = (modelId: string) => {
    setSelectedModelIds((prev) =>
      prev.includes(modelId) ? prev.filter((id) => id !== modelId) : [...prev, modelId]
    );
  };

  const handleSelectAllModels = () => {
    if (selectedModelIds.length === filteredModels.length) {
      setSelectedModelIds([]);
    } else {
      setSelectedModelIds(filteredModels.map((m) => m.id));
    }
  };

  // Create serie
  const handleCreateSerie = async () => {
    if (!selectedMakeId || !newSerieLabel.trim()) return;

    try {
      setCreating(true);
      await api.post('series/create', {
        make_id: selectedMakeId,
        label: newSerieLabel.trim(),
      });

      setSnackbar({
        open: true,
        message: `Serie "${newSerieLabel}" created successfully`,
        severity: 'success',
      });

      setCreateDialogOpen(false);
      setNewSerieLabel('');

      // Refresh data
      hasFetched.current = false;
      await fetchData();
      hasFetched.current = true;
    } catch (err) {
      console.error('Failed to create serie:', err);
      setSnackbar({
        open: true,
        message: 'Failed to create serie',
        severity: 'error',
      });
    } finally {
      setCreating(false);
    }
  };

  // Delete serie
  const handleDeleteClick = (serie: SerieItem) => {
    setSerieToDelete(serie);
    setDeleteDialogOpen(true);
  };

  const handleDeleteSerie = async () => {
    if (!serieToDelete) return;

    try {
      setDeleting(true);
      await api.delete(`series/delete/${serieToDelete.id}`);

      setSnackbar({
        open: true,
        message: `Serie "${serieToDelete.label}" deleted successfully`,
        severity: 'success',
      });

      setDeleteDialogOpen(false);
      setSerieToDelete(null);

      if (selectedSerieId === serieToDelete.id) {
        setSelectedSerieId(null);
        setSelectedModelIds([]);
      }

      // Refresh data
      hasFetched.current = false;
      await fetchData();
      hasFetched.current = true;
    } catch (err) {
      console.error('Failed to delete serie:', err);
      setSnackbar({
        open: true,
        message: 'Failed to delete serie. It may have models attached.',
        severity: 'error',
      });
    } finally {
      setDeleting(false);
    }
  };

  // Rename serie
  const handleStartRenameSerie = (serie: SerieItem) => {
    setRenamingSerieId(serie.id);
    setRenameSerieLabel(serie.label);
  };

  const handleRenameSerie = async () => {
    if (!renamingSerieId || !renameSerieLabel.trim()) return;
    try {
      setRenamingSerie(true);
      await api.post(`series/edit/${renamingSerieId}`, { label: renameSerieLabel.trim() });
      setSnackbar({
        open: true,
        message: 'Serie renamed successfully',
        severity: 'success',
      });
      setRenamingSerieId(null);
      hasFetched.current = false;
      await fetchData();
      hasFetched.current = true;
    } catch (err) {
      console.error('Failed to rename serie:', err);
      setSnackbar({
        open: true,
        message: 'Failed to rename serie',
        severity: 'error',
      });
    } finally {
      setRenamingSerie(false);
    }
  };

  const handleRenameSerieKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleRenameSerie();
    } else if (e.key === 'Escape') {
      setRenamingSerieId(null);
    }
  };

  // Create serie & move models
  const handleOpenCreateMoveDialog = () => {
    if (selectedModelIds.length === 1) {
      const model = filteredModels.find((m) => m.id === selectedModelIds[0]);
      setCreateMoveSerieLabel(model?.label || '');
    } else {
      setCreateMoveSerieLabel('');
    }
    setCreateMoveDialogOpen(true);
  };

  const handleCreateSerieAndMove = async () => {
    if (!selectedMakeId || !createMoveSerieLabel.trim() || selectedModelIds.length === 0) return;

    try {
      setCreatingAndMoving(true);

      // Create the new serie
      const createResponse = await api.post<ApiResponse<{ id: string }>>('series/create', {
        make_id: selectedMakeId,
        label: createMoveSerieLabel.trim(),
      });
      const newSerieId = createResponse.data.results.id;

      // Move selected models to the new serie
      await api.post(`series/add-models/${newSerieId}`, {
        models: selectedModelIds,
      });

      setSnackbar({
        open: true,
        message: `Serie "${createMoveSerieLabel.trim()}" created and ${selectedModelIds.length} model(s) moved`,
        severity: 'success',
      });

      setCreateMoveDialogOpen(false);
      setCreateMoveSerieLabel('');
      setSelectedModelIds([]);

      // Refresh data
      hasFetched.current = false;
      await fetchData();
      hasFetched.current = true;
    } catch (err) {
      console.error('Failed to create serie and move models:', err);
      setSnackbar({
        open: true,
        message: 'Failed to create serie and move models',
        severity: 'error',
      });
    } finally {
      setCreatingAndMoving(false);
    }
  };

  // Move models
  const handleMoveModels = async () => {
    if (!targetSerieId || selectedModelIds.length === 0) return;

    try {
      setMoving(true);
      await api.post(`series/add-models/${targetSerieId}`, {
        models: selectedModelIds,
      });

      setSnackbar({
        open: true,
        message: `${selectedModelIds.length} model(s) moved successfully`,
        severity: 'success',
      });

      setMoveDialogOpen(false);
      setTargetSerieId('');
      setSelectedModelIds([]);

      // Refresh data
      hasFetched.current = false;
      await fetchData();
      hasFetched.current = true;
    } catch (err) {
      console.error('Failed to move models:', err);
      setSnackbar({
        open: true,
        message: 'Failed to move models',
        severity: 'error',
      });
    } finally {
      setMoving(false);
    }
  };

  const handleRefresh = () => {
    hasFetched.current = false;
    fetchData();
    hasFetched.current = true;
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const currentDrawerWidth = drawerCollapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH;

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Navigation Drawer */}
      <DrawerNavigation
        currentPage="seriesEditor"
        onNavigate={onNavigate}
        collapsed={drawerCollapsed}
        onToggleCollapse={() => setDrawerCollapsed(!drawerCollapsed)}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { xs: '100%', md: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { xs: 0, md: `${currentDrawerWidth}px` },
          transition: 'margin-left 0.2s, width 0.2s',
        }}
      >
        {/* App Bar */}
        <AppBar
          position="fixed"
          sx={{
            width: { xs: '100%', md: `calc(100% - ${currentDrawerWidth}px)` },
            ml: { xs: 0, md: `${currentDrawerWidth}px` },
            transition: 'margin-left 0.2s, width 0.2s',
          }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
              Series Editor
            </Typography>
            {user && (
              <Typography variant="body2" sx={{ mr: 2 }}>
                {user.firstName} {user.lastName}
              </Typography>
            )}
            <IconButton color="inherit" onClick={handleRefresh} title="Refresh">
              <RefreshIcon />
            </IconButton>
            <IconButton color="inherit" onClick={onLogout}>
              <LogoutIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Toolbar />

        <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 2, height: 'calc(100vh - 150px)' }}>
              {/* Makes Column */}
              <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="h6">Makes ({filteredMakes.length})</Typography>
                  <TextField
                    size="small"
                    placeholder="Search makes..."
                    value={makeSearch}
                    onChange={(e) => setMakeSearch(e.target.value)}
                    fullWidth
                    sx={{ mt: 1 }}
                  />
                </Box>
                <List sx={{ overflow: 'auto', flexGrow: 1 }}>
                  {filteredMakes.map((make) => (
                    <ListItem key={make.id} disablePadding>
                      <ListItemButton
                        selected={selectedMakeId === make.id}
                        onClick={() => handleMakeClick(make.id)}
                      >
                        <ListItemText primary={make.label} />
                        {makesWithEmptySeries.has(make.id) && (
                          <Tooltip title="Has series without models">
                            <WarningAmberIcon fontSize="small" sx={{ color: 'warning.main', ml: 1 }} />
                          </Tooltip>
                        )}
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Paper>

              {/* Series Column */}
              <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                      Series {selectedMakeId ? `(${filteredSeries.length})` : ''}
                    </Typography>
                    {selectedMakeId && (
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => setCreateDialogOpen(true)}
                      >
                        Add
                      </Button>
                    )}
                  </Box>
                  {selectedMakeId && (
                    <TextField
                      size="small"
                      placeholder="Search series..."
                      value={serieSearch}
                      onChange={(e) => setSerieSearch(e.target.value)}
                      fullWidth
                      sx={{ mt: 1 }}
                    />
                  )}
                </Box>
                <List sx={{ overflow: 'auto', flexGrow: 1 }}>
                  {!selectedMakeId ? (
                    <ListItem>
                      <ListItemText
                        secondary="Select a make to view series"
                        sx={{ textAlign: 'center' }}
                      />
                    </ListItem>
                  ) : filteredSeries.length === 0 ? (
                    <ListItem>
                      <ListItemText
                        secondary="No series found"
                        sx={{ textAlign: 'center' }}
                      />
                    </ListItem>
                  ) : (
                    filteredSeries.map((serie) => (
                      <ListItem
                        key={serie.id}
                        disablePadding
                        secondaryAction={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStartRenameSerie(serie);
                              }}
                              title="Rename serie"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            {serie.modelCount === 0 && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteClick(serie);
                                }}
                                color="error"
                                title="Delete serie"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        }
                      >
                        <ListItemButton
                          selected={selectedSerieId === serie.id}
                          onClick={() => handleSerieClick(serie.id)}
                        >
                          {renamingSerieId === serie.id ? (
                            <TextField
                              size="small"
                              value={renameSerieLabel}
                              onChange={(e) => setRenameSerieLabel(e.target.value)}
                              onKeyDown={handleRenameSerieKeyDown}
                              onBlur={handleRenameSerie}
                              autoFocus
                              disabled={renamingSerie}
                              onClick={(e) => e.stopPropagation()}
                              sx={{ mr: 1 }}
                            />
                          ) : (
                            <ListItemText primary={serie.label} />
                          )}
                          <Chip
                            label={serie.modelCount}
                            size="small"
                            sx={{ ml: 1, mr: 6 }}
                            color={serie.modelCount === 0 ? 'default' : 'primary'}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))
                  )}
                </List>
              </Paper>

              {/* Models Column */}
              <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6" sx={{ flexGrow: 1 }}>
                    Models {selectedSerieId ? `(${filteredModels.length})` : ''}
                  </Typography>
                  {selectedSerieId && filteredModels.length > 0 && (
                    <Button
                      size="small"
                      onClick={handleSelectAllModels}
                    >
                      {selectedModelIds.length === filteredModels.length ? 'Deselect All' : 'Select All'}
                    </Button>
                  )}
                </Box>

                {/* Action buttons */}
                {selectedModelIds.length > 0 && (
                  <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider', bgcolor: 'action.selected', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {availableTargetSeries.length > 0 && (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => setMoveDialogOpen(true)}
                      >
                        Move {selectedModelIds.length} model(s) to another serie
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      size="small"
                      color="success"
                      startIcon={<AddIcon />}
                      onClick={handleOpenCreateMoveDialog}
                    >
                      Create serie &amp; move
                    </Button>
                  </Box>
                )}

                <List sx={{ overflow: 'auto', flexGrow: 1 }}>
                  {!selectedSerieId ? (
                    <ListItem>
                      <ListItemText
                        secondary="Select a serie to view models"
                        sx={{ textAlign: 'center' }}
                      />
                    </ListItem>
                  ) : filteredModels.length === 0 ? (
                    <ListItem>
                      <ListItemText
                        secondary="No models in this serie"
                        sx={{ textAlign: 'center' }}
                      />
                    </ListItem>
                  ) : (
                    filteredModels.map((model) => (
                      <ListItem key={model.id} disablePadding>
                        <ListItemButton onClick={() => handleModelToggle(model.id)}>
                          <Checkbox
                            edge="start"
                            checked={selectedModelIds.includes(model.id)}
                            tabIndex={-1}
                            disableRipple
                          />
                          <ListItemText primary={model.label} />
                        </ListItemButton>
                      </ListItem>
                    ))
                  )}
                </List>
              </Paper>
            </Box>
          )}
        </Container>
      </Box>

      {/* Create Serie Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Create New Serie</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Serie Name"
            fullWidth
            value={newSerieLabel}
            onChange={(e) => setNewSerieLabel(e.target.value)}
            disabled={creating}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)} disabled={creating}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateSerie}
            variant="contained"
            disabled={creating || !newSerieLabel.trim()}
          >
            {creating ? <CircularProgress size={24} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Serie Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteSerie}
        title="Delete Serie"
        message={`Are you sure you want to delete "${serieToDelete?.label}"?`}
        confirmLabel="Delete"
        loading={deleting}
      />

      {/* Create Serie & Move Dialog */}
      <Dialog
        open={createMoveDialogOpen}
        onClose={() => setCreateMoveDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Create Serie &amp; Move Models</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Create a new serie and move {selectedModelIds.length} model(s) to it.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="New Serie Name"
            fullWidth
            value={createMoveSerieLabel}
            onChange={(e) => setCreateMoveSerieLabel(e.target.value)}
            disabled={creatingAndMoving}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateMoveDialogOpen(false)} disabled={creatingAndMoving}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateSerieAndMove}
            variant="contained"
            color="success"
            disabled={creatingAndMoving || !createMoveSerieLabel.trim()}
          >
            {creatingAndMoving ? <CircularProgress size={24} /> : 'Create & Move'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Move Models Dialog */}
      <Dialog
        open={moveDialogOpen}
        onClose={() => setMoveDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Move Models</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Move {selectedModelIds.length} model(s) to:
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Target Serie</InputLabel>
            <Select
              value={targetSerieId}
              onChange={(e) => setTargetSerieId(e.target.value)}
              label="Target Serie"
              disabled={moving}
            >
              {availableTargetSeries.map((serie) => (
                <MenuItem key={serie.id} value={serie.id}>
                  {serie.label} ({serie.modelCount} models)
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoveDialogOpen(false)} disabled={moving}>
            Cancel
          </Button>
          <Button
            onClick={handleMoveModels}
            variant="contained"
            disabled={moving || !targetSerieId}
          >
            {moving ? <CircularProgress size={24} /> : 'Move'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default SeriesEditor;
